import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Button,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { Select } from "@ofqual/portal-components/components/Select";
import React, { useMemo, useState, useEffect } from "react";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { Controller, useForm } from "react-hook-form";
import {
  foiEventTypeService,
  usersService
} from "../../../services";
import { authSelector } from "../../../redux/selectors";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { FoiDialogProps } from "../../activityFeed/dialogs/DialogProps";
import { useDispatch, useSelector } from "react-redux";
import { addFoiEvent } from "../../../redux/actions";
import { DateTimePicker } from "@ofqual/portal-components/components/DateTimePicker";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Model } from "@ofqual/portal-core/core/types";
import { Error } from "../../common/Error";
import {
  getLookupTracker
} from "@ofqual/portal-core/core/utils/modelHelpers";

import { fieldLabels } from "../../sections/fieldLabels";
import { FoiEvent } from "../../../domain/FoiEvent";

const useStyles = makeStyles((theme) => ({
  changeDetails: {
    padding: 15,
  },
  actions: {
    padding: 15,
  },
  disabledLink: {
    color: "inherit",
    textDecoration: "none",
  },
}));

export const defaultValues: Partial<FoiEvent> = {
  meetingDate: null,
  foiEventType: null,
  eventStage: 0
};

export const AddEventDialog: React.FC<FoiDialogProps> = ({
  foiItem,
  onClose,
  isAdmin,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [isSaveInProgress, setIsSaveInProgress] = useState<boolean>(false);

  const {
    handleSubmit,
    control,
    errors
  } = useForm<FoiEvent>({
    defaultValues,
  });

  const [upns, setUpns] = useState([] as Model[]);
  const { token } = useSelector(authSelector);
  useEffect(() => {
    usersService.getUsers({ token }).then((response) => setUpns(response));
  }, [token]);

  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const [foiEventTypeLookups] = useLookup(foiEventTypeService);

  const onSubmit = (newEvent: FoiEvent) => {
    setIsSaveInProgress(true);

    let recipients: string[] = [];
    recipients.forEach((r) => recipients.push(r));
    dispatch(
      addFoiEvent(foiItem?.id!,
        newEvent)
    );
    onClose();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="add-event-dialog"
    >
      <DialogTitle data-test="add-event-title">Add an event</DialogTitle>
      <Divider component="div" />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={classes.changeDetails}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="recipients"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ onChange, value, ...props }) => (
                    <Autocomplete
                      multiple
                      autoSelect
                      aria-required="true"
                      options={upns}
                      ChipProps={{ color: "primary" }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          data-test="add-event-recipients-field"
                          name="recipients"
                          variant="outlined"
                          label="Recipients"
                        />
                      )}
                      getOptionLabel={(option) => option.name}
                      getOptionSelected={(option, value) => option.id === value}
                      onChange={(e: any, data: any) => onChange(data)}
                      {...props}
                    />
                  )}
                />
                {errors.recipients && (
                  <Error data-test="recipients-validation">This field is required</Error>
                )}            
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="meetingDate"
                  as={
                    <DateTimePicker
                      dataTest="add-event-date"
                      label={fieldLabels["meetingDate"]}
                      required
                    />
                  }
                />
                {errors.meetingDate?.type === "required" && (
                  <Error data-test="meetingDate-validation">This field is required</Error>
                )}
              </Grid>
              <Grid item md={12}>
                <Controller
                  control={control}
                  name="foiEventType"
                  rules={{ required: true }}
                  as={
                    <Select
                      selectVariant="single"
                      label={fieldLabels["foiEventType"]}
                      datatest="foiEventType"
                      required
                      lookupTracker={lookupTracker}
                    >
                      {foiEventTypeLookups}
                    </Select>
                  }
                />
                {errors.foiEventType?.type === "required" && (
                  <Error data-test="foiEventType-validation">This field is required</Error>
                )}
              </Grid>

              <Grid item xs={12} sm={12}>
                <Controller
                  control={control}
                  rules={{ maxLength: 5000, required: true }}
                  name="message"
                  render={({ onChange, value }) => (
                    <>
                      <label>{fieldLabels.message}*</label>
                      <MarkdownEditor
                        onChange={onChange}
                        value={value}
                        readOnly={false}
                        dataTest="message-field"
                      />
                    </>
                  )}
                />
                {errors.message?.type === "maxLength" && (
                  <Error data-test="message-validation">Max length exceeded</Error>
                )}
                {errors.message?.type === "required" && (
                  <Error data-test="message-validation">This field is required</Error>
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="files"
                  as={
                    <FileInput
                      multiple={true}
                      dataTest="create-event-add-attachment-button" 
                    />
                  }
                />
              </Grid>

            </Grid>
          </div>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          data-test="cancel-event-button"
          color="primary"
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit(onSubmit)}
          data-test="add-event-modal-button"
          className={isSaveInProgress ? classes.disabledLink : undefined}
          disabled={isSaveInProgress}
          color="primary"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
